import { sum } from 'lodash';
import {
  BlocksGroup,
  BlocksNode,
  BlocksReference,
  BlocksVisualState,
} from './types';

export const hasVisualState = (
  item: BlocksNode | BlocksReference,
  visualState: BlocksVisualState
): boolean => {
  return (item.visualState & visualState) !== 0;
};

export const setVisualState = (
  item: BlocksNode | BlocksReference,
  visualState: BlocksVisualState,
  value: boolean
) => {
  if (value) {
    item.visualState = item.visualState | visualState;
  } else {
    item.visualState = item.visualState & ~visualState;
  }
};

export const firstVisibleSublabel = (node: BlocksNode): number => {
  return isGroup(node) && isNodeOpen(node) ? 1 : 2;
};

/** truthy if the node is a synthetic group. right now there's only one; the root node and that's what's tested for */
export const isSyntheticGroup = (node: BlocksNode): node is BlocksGroup =>
  isGroup(node) && !node.parent;

export const isGroup = (node: BlocksNode): node is BlocksGroup =>
  Array.isArray(node.children);

export const isNode = (
  item: BlocksNode | BlocksReference
): item is BlocksNode => 'parent' in item;

export const isNodeHitVisible = (node: BlocksNode): boolean =>
  !isNodeRoot(node) && !hasVisualState(node, BlocksVisualState.Hidden);

export const isReference = (
  item: BlocksNode | BlocksReference
): item is BlocksReference => !('parent' in item);

export const isReferenceHitVisible = (reference: BlocksReference) => {
  return !hasVisualState(reference, BlocksVisualState.Hidden);
};

export const isNodeOpen = (node: BlocksNode) => {
  return node.open !== undefined && node.open;
};

export const isNodeRoot = (node: BlocksNode) => {
  return !node.parent;
};

/** true if the node is explicitly marked as synthetic or if the node is the root */
export const isNodeSynthetic = (node: BlocksNode) => {
  return node.isSynthetic === true || !node.parent;
};

/** iterate visible descendants of node, including node itself */
export function* getVisibleDescendants(
  node: BlocksNode
): Generator<BlocksNode, void, unknown> {
  if (isGroup(node) && isNodeOpen(node)) {
    for (const child of node.children) {
      yield* getVisibleDescendants(child);
    }
  }

  if (!isNodeRoot(node)) {
    yield node;
  }
}

/** iterate all descendants of node, including node itself */
export function* getDescendants(
  node: BlocksNode
): Generator<BlocksNode, void, unknown> {
  if (isGroup(node)) {
    for (const child of node.children) {
      yield* getVisibleDescendants(child);
    }
  }

  if (!isNodeRoot(node)) {
    yield node;
  }
}

export const getOpenAscendant = (node: BlocksNode) => {
  let ret = node;
  let currentNode = node;
  while (currentNode.parent) {
    if (!isNodeOpen(currentNode.parent)) {
      ret = currentNode.parent;
    }

    currentNode = currentNode.parent!;
  }

  return ret;
};

/* get the lowest common parent/child ancestor of a and b */
export const getLCA = (a: BlocksNode | null, b: BlocksNode | null) => {
  const depth = (n: BlocksNode | null) => {
    let ret = 0;

    let nn = n;
    for (ret = 0; nn !== null; nn = nn.parent) {
      ++ret;
    }

    return ret;
  };

  const da = depth(a);
  const db = depth(b);
  let aa = a;
  let bb = b;
  for (let i = 0; i < da - db; ++i) {
    aa = aa!.parent;
  }

  for (let i = 0; i < db - da; ++i) {
    bb = bb!.parent;
  }

  while (aa!.parent !== bb!.parent) {
    aa = aa!.parent;
    bb = bb!.parent;
  }

  return aa!.parent;
};

/* verify that ascendant is an ascendant of node */
export const isAscendant = (ascendant: BlocksNode, node: BlocksNode) => {
  let n: BlocksNode | null = node;

  while (n) {
    if (n === ascendant) {
      return true;
    }

    n = n.parent;
  }

  return false;
};

/** verify that entire ascendency (not including the node itself) is open */
export const isAscendancyOpen = (node: BlocksNode) => {
  let currentNode = node;
  while (currentNode.parent) {
    if (!isNodeOpen(currentNode.parent!)) {
      return false;
    }

    currentNode = currentNode.parent;
  }

  return true;
};

/** return the total number of descendants of the specified node */
export const getDescendantCount = (node: BlocksNode): number =>
  node.children
    ? sum(node.children.map(child => 1 + getDescendantCount(child)))
    : 0;
